// Point budget for the experimental "budget" point cloud LOD strategy.
//
// The server refines point cloud octree nodes nearest and sparsest on screen first and stops at this
// many points, so the budget is the only knob. "Auto" scales it with the drawing buffer. Points spread
// over the whole visible scene, not only the surfaces in front: on a measured interior scan
// (Mechanical-room, 1280 x 720) near surfaces stopped improving at about 7.4 M points, eight per pixel.
// The caps keep GPU memory (about 32 bytes per point) and the download within what phones and desktops
// handle.

export interface HsPointCloudBudgetEnvironment {
  /** Drawing buffer size in physical pixels. */
  drawingBufferWidth: number;
  drawingBufferHeight: number;
  maxTouchPoints?: number;
  deviceMemory?: number;
  coarsePointer?: boolean;
}

export const HS_POINT_CLOUD_BUDGET_POINTS_PER_PIXEL: number = 8;
export const HS_POINT_CLOUD_BUDGET_MIN_POINTS: number = 1_000_000;
export const HS_DESKTOP_POINT_CLOUD_BUDGET_MAX_POINTS: number = 10_000_000;
export const HS_MOBILE_POINT_CLOUD_BUDGET_MAX_POINTS: number = 2_000_000;
export const HS_LOW_MEMORY_POINT_CLOUD_BUDGET_MAX_POINTS: number = 3_000_000;

/**
 * Share of the rendered point size used as the target screen spacing: points then overlap enough to
 * close surfaces. On Mechanical-room, spacing = point size left visible gaps (near-surface fill 0.908);
 * two thirds of it closed them (0.935) and smaller targets only added downloads.
 */
export const HS_POINT_CLOUD_TARGET_SPACING_SHARE: number = 2 / 3;

/**
 * Share of the point size used as the spacing floor (`minSpacingPixels`). Equal to the target share, so no
 * block finer than the target is downloaded: a floor of one third of the point size on demodev (Mechanical-room,
 * 45k nodes) loaded 4.1 M instead of 0.9 M points for the exterior view and 38% more bytes on the interior path
 * without changing the measured surface fill (0.778 vs 0.777 outside, 0.936 vs 0.935 inside). Hosts that draw
 * sub-pixel detail (hole filling, larger displays) can send a lower value.
 */
export const HS_POINT_CLOUD_MIN_SPACING_SHARE: number = HS_POINT_CLOUD_TARGET_SPACING_SHARE;

/**
 * Point budget for `requested` points, or the automatic budget when `requested` is 0 or not a positive
 * finite number.
 */
export function hsResolvePointCloudBudget(
  requested: number,
  environment: HsPointCloudBudgetEnvironment = readEnvironment()
): number {
  if (Number.isFinite(requested) && requested > 0) {
    return Math.round(requested);
  }
  return hsAutoPointCloudBudget(environment);
}

export function hsAutoPointCloudBudget(environment: HsPointCloudBudgetEnvironment = readEnvironment()): number {
  const pixels: number = Math.max(0, environment.drawingBufferWidth) * Math.max(0, environment.drawingBufferHeight);
  const mobile: boolean = (environment.maxTouchPoints ?? 0) > 0 && environment.coarsePointer === true;
  const lowMemory: boolean = environment.deviceMemory !== undefined && environment.deviceMemory <= 4;
  const max: number = mobile
    ? HS_MOBILE_POINT_CLOUD_BUDGET_MAX_POINTS
    : lowMemory
    ? HS_LOW_MEMORY_POINT_CLOUD_BUDGET_MAX_POINTS
    : HS_DESKTOP_POINT_CLOUD_BUDGET_MAX_POINTS;
  const byScreen: number = Math.round((pixels * HS_POINT_CLOUD_BUDGET_POINTS_PER_PIXEL) / 100_000) * 100_000;
  return Math.min(max, Math.max(HS_POINT_CLOUD_BUDGET_MIN_POINTS, byScreen));
}

/** The device side of the auto budget: touch input, coarse pointer and device memory of this browser. */
export function hsPointCloudBudgetDevice(): Omit<
  HsPointCloudBudgetEnvironment,
  'drawingBufferWidth' | 'drawingBufferHeight'
> {
  const scope: { navigator?: Navigator & { deviceMemory?: number }; matchMedia?: (query: string) => MediaQueryList } =
    globalThis as typeof globalThis & { navigator?: Navigator & { deviceMemory?: number } };
  return {
    maxTouchPoints: scope.navigator?.maxTouchPoints,
    deviceMemory: scope.navigator?.deviceMemory,
    coarsePointer: scope.matchMedia?.('(pointer: coarse)').matches,
  };
}

function readEnvironment(): HsPointCloudBudgetEnvironment {
  const scope: { screen?: Screen; devicePixelRatio?: number } = globalThis;
  const ratio: number = scope.devicePixelRatio ?? 1;
  return {
    ...hsPointCloudBudgetDevice(),
    drawingBufferWidth: (scope.screen?.width ?? 1920) * ratio,
    drawingBufferHeight: (scope.screen?.height ?? 1080) * ratio,
  };
}
